import { useRef, useCallback, useState, useLayoutEffect, type RefCallback } from 'react'
import { useIsMobileVariant } from '@dhl-ui/components'

const isInputElement = (elem: Element | null): boolean =>
  elem instanceof HTMLElement &&
  ((elem.tagName === 'INPUT' &&
    !['button', 'submit', 'checkbox', 'file', 'image'].includes((elem as HTMLInputElement).type)) ||
    elem.tagName === 'TEXTAREA' ||
    elem.tagName === 'SELECT' ||
    elem.isContentEditable ||
    elem.getAttribute('role') === 'textbox' ||
    elem.getAttribute('role') === 'combobox')

/**
 * Scroll focused inputs into view when the on-screen keyboard opens on iOS.
 *
 * On iOS WKWebView with `position: fixed` on the body (used to prevent
 * the iOS bounce/rubber-banding effect), the browser's native
 * scroll-to-focused-input behaviour is disabled. This hook re-implements
 * that behaviour using `Element.scrollIntoView()`.
 *
 * Strategy:
 * - On the **first** focus of an input (keyboard not yet visible), a 500 ms
 *   delay is applied before scrolling to give the keyboard animation time to
 *   settle. There is no reliable cross-platform event for "keyboard fully
 *   open", so a fixed delay is the pragmatic workaround.
 * - On **subsequent** focuses (e.g. tabbing between inputs while the keyboard
 *   is already open), scrolling happens immediately since the layout is
 *   already stable.
 * - When focus leaves all inputs inside the container the keyboard-visible
 *   flag is reset, so the next focus will again wait for the keyboard.
 *
 * Returns a **callback ref** that should be placed on the container element.
 * Using a callback ref (backed by state) ensures the effect re-runs when the
 * DOM element appears — important for elements rendered inside modals or other
 * components that delay-mount their children.
 */

export const useScrollInputIntoView = (): RefCallback<HTMLDivElement | null> => {
  const isKeyboardVisible = useRef(false)
  const isMobileVariant = useIsMobileVariant()
  const [container, setContainer] = useState<HTMLDivElement | null>(null)
  const windowHeight = useRef(window.innerHeight)

  useLayoutEffect(() => {
    if (!isMobileVariant || !container) {
      return undefined
    }

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as Element

      if (!isInputElement(target)) {
        return
      }

      const scrollToTarget = () => target.scrollIntoView({ block: 'center', behavior: 'smooth' })

      if (isKeyboardVisible.current) {
        scrollToTarget()
      } else {
        // On iOS there is no way of knowing if the keyboard is fully open when first focusing an input,
        // so scrolling to the input directly would still result in the input being covered by the keyboard.
        // By adding a delay before the scroll, we can ensure the target element is visible after the keyboard
        // animation has completed. Not ideal, but it's necessary to work around the platform limitations.
        setTimeout(() => {
          scrollToTarget()

          const visualViewPortHeight = window.visualViewport?.height ?? windowHeight.current

          if ((windowHeight.current - visualViewPortHeight) / windowHeight.current > 0.35) {
            // We'll assume the keyboard size to be at least 35% of the screen height. If ratio between the innerHeight and
            // visualViewport height exceeds that percentage, we can assume the keyboard is open. This check is necessary
            // to avoid setting the keyboard visible flag when focusing an input that doesn't trigger the keyboard
            // (e.g. a date input with a custom picker).
            isKeyboardVisible.current = true
          }
        }, 500)
      }
    }

    const handleFocusOut = (e: FocusEvent) => {
      // Check if focus is moving to another input in the container
      const next = e.relatedTarget as Element | null

      if (!isKeyboardVisible.current || (next && isInputElement(next) && container.contains(next))) {
        // Focus moving between inputs - keep keyboard state
        return
      }

      isKeyboardVisible.current = false
    }

    container.addEventListener('focusin', handleFocusIn, { passive: true })
    container.addEventListener('focusout', handleFocusOut, { passive: true })

    return () => {
      container.removeEventListener('focusin', handleFocusIn)
      container.removeEventListener('focusout', handleFocusOut)
    }
  }, [container, isMobileVariant, windowHeight])

  return useCallback((node: HTMLDivElement | null) => setContainer(node), [])
}
